import {
  IContactSerialized,
  ITenancyCreate,
  ITenancyCreateSerialized,
} from "@/mutators/tenancy/TenancyMutator.types"
import { capitalize } from "@/helpers/stringHelpers"
import { IContact } from "@/models/contacts/contact.types"
import {
  ChangeOfTenancyRequestDto,
  ContactDetailsDto,
  DirectDebitInfoDto,
} from "@/api/backofficeRest/generated/model"

const getContactPhones = (contact: IContact) => ({
  phone1: contact.phone1 || contact.phone2 || "",
  phone2: contact.phone1 ? contact.phone2 || "" : "",
})

export const getTenancyContacts = (
  data: ITenancyCreate,
): IContactSerialized[] => {
  if (data.isMovingIn) {
    if (data.newOccupierContacts.length === 1) {
      data.newOccupierContacts[0] = {
        ...data.newOccupierContacts[0],
        juniferPrimaryContact: true,
      }
    }
    return data.newOccupierContacts.map((contact) => ({
      contactType: "Residential",
      title: contact.title,
      email: contact.email,
      billingMethod: "eBilling",
      ...getContactPhones(contact),
      primaryContact: contact.juniferPrimaryContact,
      forename: capitalize(contact.firstName),
      surname: capitalize(contact.lastName),
    }))
  }

  return [getPrimaryContact(data)]
}

export const getForwardingAddress = (
  data: ITenancyCreate,
  serializedData: ITenancyCreateSerialized,
): ITenancyCreateSerialized => {
  if (data.isMovingIn) {
    return {
      ...serializedData,
      forwarding_address: {
        address1: data.account.supplyAddress?.addressLine1 ?? "",
        address2: data.account.supplyAddress?.addressLine2 ?? "",
        careOf: data.account.name,
        postcode: data.account.supplyAddress?.postcode ?? "",
      },
    }
  }

  return {
    ...serializedData,
    forwarding_address: {
      address1: data.forwardAddress.addressLine1,
      address2: data.forwardAddress.addressLine2,
      careOf: data.forwardAddress.careOf,
      postcode: data.forwardAddress.postcode,
    },
  }
}

export const getForwardingContact = (
  data: ITenancyCreate,
  serializedData: ITenancyCreateSerialized,
): ITenancyCreateSerialized => {
  if (data.isMovingIn) {
    return {
      ...serializedData,
    }
  }
  return {
    ...serializedData,
    forwarding_contact: getPrimaryContact(data),
  }
}

export const getDirectDebitInfo = (
  data: ITenancyCreate,
  serializedData: ITenancyCreateSerialized,
): ITenancyCreateSerialized => {
  if (data.isMovingIn && data.directDebitDetails) {
    return {
      ...serializedData,
      direct_debit_info: {
        bankAccountSortCode: data.directDebitDetails.sortCode,
        bankAccountNumber: data.directDebitDetails.accountNumber,
        bankAccountName: data.directDebitDetails.accountName,
        regularPaymentAmount: data.directDebitDetails.amount,
        regularPaymentDayOfMonth: data.directDebitDetails.nextPaymentDt,
        seasonalPaymentFl: data.directDebitDetails.seasonalPaymentFl,
      },
    }
  }

  return serializedData
}

export const getPrimaryContact = (data: ITenancyCreate) => ({
  billingMethod: "eBilling",
  contactType: "Residential",
  title: data.forwardAddress.title,
  forename: data.primaryAccountContact?.firstName ?? "",
  surname: data.primaryAccountContact?.lastName ?? "",
  phone1:
    data.primaryAccountContact?.phone1 ??
    data.primaryAccountContact?.phone2 ??
    "",
  phone2: data.primaryAccountContact?.phone2 ?? "",
  email: data.primaryAccountContact?.email ?? "",
  primaryContact: !!data.primaryAccountContact?.juniferPrimaryContact,
})

export const getPrimaryContactMoveOut = (data: ITenancyCreate) => ({
  title: data.forwardAddress.title,
  forename: data.primaryAccountContact?.firstName ?? "",
  surname: data.primaryAccountContact?.lastName ?? "",
  phone1:
    data.primaryAccountContact?.phone1 ??
    data.primaryAccountContact?.phone2 ??
    "",
  phone2: data.primaryAccountContact?.phone2 ?? "",
  email: data.primaryAccountContact?.email ?? "",
  primaryContact: !!data.primaryAccountContact?.juniferPrimaryContact,
  receive_email: data.primaryAccountContact?.receiveEmail ?? undefined,
})

export const getTenancyContactsMoveOut = (
  data: ITenancyCreate,
): ContactDetailsDto[] => {
  if (data.isMovingIn) {
    if (data.newOccupierContacts.length === 1) {
      data.newOccupierContacts[0] = {
        ...data.newOccupierContacts[0],
        juniferPrimaryContact: true,
      }
    }
    return data.newOccupierContacts.map((contact) => ({
      title: contact.title,
      email: contact.email,
      ...getContactPhones(contact),
      primaryContact: contact.juniferPrimaryContact,
      forename: capitalize(contact.firstName),
      surname: capitalize(contact.lastName),
      receive_email: data.isMovingIn
        ? (contact.receiveEmail ?? undefined)
        : false,
    }))
  }

  return [getPrimaryContactMoveOut(data)]
}

export const getForwardingAddressMoveOut = (
  data: ITenancyCreate,
  serializedData: ChangeOfTenancyRequestDto,
): ChangeOfTenancyRequestDto => {
  if (data.isMovingIn) {
    let careOf = data.account.name

    if (!data.account.name) {
      careOf = `${data.primaryAccountContact?.title ?? ""} ${
        data.primaryAccountContact?.firstName ?? ""
      } ${data.primaryAccountContact?.lastName}`
    }
    return {
      ...serializedData,
      forwarding_address: {
        address1: data.account.supplyAddress?.addressLine1 ?? "",
        address2: data.account.supplyAddress?.addressLine2 ?? "",
        careOf,
        postcode: data.account.supplyAddress?.postcode ?? "",
      },
    }
  }

  return {
    ...serializedData,
    forwarding_address: {
      address1: data.forwardAddress.addressLine1,
      address2: data.forwardAddress.addressLine2,
      careOf: data.forwardAddress.careOf,
      postcode: data.forwardAddress.postcode,
    },
  }
}

export const getForwardingContactMoveOut = (
  data: ITenancyCreate,
  serializedData: ChangeOfTenancyRequestDto,
): ChangeOfTenancyRequestDto => {
  if (data.isMovingIn) {
    return {
      ...serializedData,
    }
  }
  return {
    ...serializedData,
    forwarding_contact: getPrimaryContactMoveOut(data),
  }
}

export const getDirectDebitInfoMoveOut = (
  data: ITenancyCreate,
  serializedData: ChangeOfTenancyRequestDto,
): ChangeOfTenancyRequestDto => {
  if (data.isMovingIn && data.directDebitDetails) {
    return {
      ...serializedData,
      direct_debit_info: <DirectDebitInfoDto>{
        bankAccountName: data.directDebitDetails.accountName,
        bankAccountNumber: data.directDebitDetails.accountNumber,
        bankAccountSortCode: data.directDebitDetails.sortCode,
        regularPaymentAmount: data.directDebitDetails.amount,
        regularPaymentDayOfMonth: data.directDebitDetails.nextPaymentDt,
        seasonalPaymentFl: data.directDebitDetails.seasonalPaymentFl,
      },
    }
  }
  return serializedData
}

export const getTariffInfoMoveOut = (
  data: ITenancyCreate,
  serializedData: ChangeOfTenancyRequestDto,
): ChangeOfTenancyRequestDto => {
  if (data.isMovingIn) {
    return {
      ...serializedData,
      electricity_product_ref: data.tariffInfo?.electricity,
      gas_product_ref: data.tariffInfo?.gas,
    }
  }

  return serializedData
}
